import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollReveal } from '../components/ScrollReveal';

// Image URLs
const IMAGES = {
  brain: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/a3w58nwq_IMG_0362.JPG",
  ropeBridge: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/lsvbl5jn_IMG_0349.jpg",
  greenhouse: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/0miovp04_IMG_0176%203.WEBP",
  lightbulb: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/okroebfp_IMG_0350.JPG",
};

export default function Academics() {
  return (
    <div data-testid="academics-page" className="page-transition pt-24">
      {/* Hero Section */}
      <section className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <ScrollReveal>
                <p className="text-sm font-medium uppercase tracking-wider text-[#C27856] mb-4">
                  Academics
                </p>
              </ScrollReveal>
              
              <ScrollReveal delay={100}>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A3C34] leading-[1.1]">
                  Mastery Over Memorisation
                </h1>
              </ScrollReveal>
              
              <ScrollReveal delay={200}>
                <p className="mt-8 text-base font-light leading-relaxed text-[#2D3A3A]">
                  Measurable mastery.
                </p>
              </ScrollReveal>
            </div>
            
            <ScrollReveal delay={150}>
              <div className="img-hover-zoom rounded-2xl overflow-hidden">
                <img 
                  src={IMAGES.brain}
                  alt="Student focused on learning"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Mastery Approach Section */}
      <section data-testid="mastery-approach-section" className="py-28 md:py-36 bg-[#F2F0E9]">
        <div className="container-nova">
          <div className="max-w-3xl mx-auto">
            <ScrollReveal>
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] text-center">
                The Mastery Block
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <p className="mt-8 text-base font-light leading-relaxed text-[#2D3A3A] text-center">
                Two hours daily. Literacy and numeracy foundations. Individual pace—no rushing, no holding back.
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="mt-16 grid md:grid-cols-2 gap-10">
                <div className="bg-white p-8 rounded-2xl border border-[#EBE8E0]">
                  <h3 className="font-heading text-base font-medium text-[#1A3C34] mb-3">
                    Struggling learners
                  </h3>
                  <p className="text-[#5C6B6B] font-light text-sm">
                    Early intervention with targeted support. No child moves on until understanding is demonstrated. No one left behind.
                  </p>
                </div>
                
                <div className="bg-white p-8 rounded-2xl border border-[#EBE8E0]">
                  <h3 className="font-heading text-base font-medium text-[#1A3C34] mb-3">
                    Advanced learners
                  </h3>
                  <p className="text-[#5C6B6B] font-light text-sm">
                    Progression based on demonstrated fluency, not age or calendar. No ceiling on growth.
                  </p>
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={300}>
              <div className="mt-16 p-8 bg-[#1A3C34] rounded-2xl">
                <h3 className="font-heading text-base font-medium text-[#F9F8F4] mb-4 text-center">
                  Rigour Without Burnout
                </h3>
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <p className="text-[#C27856] text-sm font-medium mb-2">Morning (2 Hours)</p>
                    <ul className="space-y-2 text-[#F9F8F4]/80 text-sm font-light">
                      <li>• Literacy & numeracy foundations</li>
                      <li>• Real-time progress tracking</li>
                      <li>• Personalised learning paths</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-[#C27856] text-sm font-medium mb-2">Remainder of Day</p>
                    <ul className="space-y-2 text-[#F9F8F4]/80 text-sm font-light">
                      <li>• Life skills and independence</li>
                      <li>• Agriculture & sustainability</li>
                      <li>• Trade, craft & problem-solving</li>
                    </ul>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Environment Section */}
      <section data-testid="environment-section" className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <ScrollReveal>
              <div className="img-hover-zoom rounded-2xl overflow-hidden">
                <img 
                  src={IMAGES.ropeBridge}
                  alt="Natural outdoor environment"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
              </div>
            </ScrollReveal>
            
            <div>
              <ScrollReveal>
                <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34]">
                  The Environment
                </h2>
              </ScrollReveal>
              
              <ScrollReveal delay={100}>
                <p className="mt-8 text-base font-light leading-relaxed text-[#2D3A3A]">
                  Located in the Runda / Gigiri corridor of Nairobi. A carefully selected residential setting designed for learning.
                </p>
              </ScrollReveal>
              
              <ScrollReveal delay={200}>
                <ul className="mt-10 space-y-3">
                  {[
                    'Natural light throughout',
                    'Neutral, calming tones',
                    'Indoor-outdoor integration',
                    'Functional simplicity',
                    'Space for agriculture and outdoor learning'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-[#2D3A3A]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C27856] mt-2 flex-shrink-0" />
                      <span className="font-light text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Stability & Governance Section */}
      <section data-testid="governance-section" className="py-28 md:py-36 bg-[#F2F0E9]">
        <div className="container-nova">
          <div className="max-w-2xl mx-auto">
            <ScrollReveal>
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] text-center">
                Stability & Governance
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <div className="mt-16 space-y-6">
                <p className="text-sm font-light leading-relaxed text-[#2D3A3A]">
                  Registered private educational institution in compliance with Kenyan requirements.
                </p>
                
                <p className="text-sm font-light leading-relaxed text-[#5C6B6B]">
                  Conservative pilot structure. Defined capacity of 32 learners. Disciplined adult-to-child ratios.
                </p>
                
                <p className="text-sm font-light leading-relaxed text-[#5C6B6B]">
                  Capital supports environment setup and system refinement. Growth informed by measurable outcomes.
                </p>
                
                <p className="text-sm font-light leading-relaxed text-[#5C6B6B]">
                  Defined safeguarding and health standards appropriate for a residential learning environment.
                </p>
                
                <p className="text-sm font-medium leading-relaxed text-[#1A3C34] mt-12 pt-8 border-t border-[#1A3C34]/10">
                  The pilot will not launch unless defined operational thresholds are met.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#1A3C34]">
        <div className="container-nova text-center">
          <ScrollReveal>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-[#F9F8F4] max-w-xl mx-auto">
              Admissions
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={250}>
            <Link 
              to="/admissions" 
              className="mt-10 inline-block bg-[#F9F8F4] text-[#1A3C34] hover:bg-white px-10 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
              data-testid="admissions-cta-btn"
            >
              View Admissions
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
